import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ReactComponent as ProgramIcon } from '../../assets/svg/ProgramIcon.svg'
import { ReactComponent as SurveyContentIcon } from '../../assets/svg/SurveyContentIcon.svg'
import { ReactComponent as JournalContentIcon } from '../../assets/svg/JournalContentIcon.svg'
import { ReactComponent as SearchMagnifyIcon } from '../../assets/svg/SearchMagnifyIcon.svg'
import { useTranslation } from 'react-i18next'
import SingleProgram from './ToGo/SingleProgram'
import SingleSurvey from './ToGo/SingleSurvey'
import SingleJournal from './ToGo/SingleJournal'
import RunningProgram from './ToGo/RunningProgram'
import ContentTopicButtons from './ContentTopicButtons'
import TypeHomeList from './TypeHomeList'
import TopicHomeList from './TopicHomeList'
import SearchResults from './SearchResults'
import { ContentSkeleton } from './ToGo/ContentCard'
import { usePageTitle } from '../../utils/util'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'
import useReadyToGoContent, { filterList, filterTopicContent } from './useReadyToGoContent'
import { getRunningPrograms } from '../../server/server'
import { useUser } from '../../auth'

const LIBRARY_TYPE_EVENTS = {
    Programs: ANALYTICS_EVENTS.CONTENT_LIBRARY_PROGRAMS_VIEWED,
    Surveys: ANALYTICS_EVENTS.CONTENT_LIBRARY_SURVEYS_VIEWED,
    Journals: ANALYTICS_EVENTS.CONTENT_LIBRARY_JOURNALS_VIEWED,
}

const CONTENT_LIST = [
    {
        name: 'Programs',
        description: 'Choose from our range of ready-to-go programs designed to support your students and save you time',
        icon: <ProgramIcon />,
    },
    {
        name: 'Surveys',
        description: 'Each of our ready-to-go surveys is designed to help you understand your students better',
        icon: <SurveyContentIcon />,
    },
    {
        name: 'Journals',
        description: 'Pick a journal that will keep your students engaged',
        icon: <JournalContentIcon />,
    },
]

const MODES = CONTENT_LIST.map((c) => c.name)
const DEFAULT_MODE = 'Programs'
const DEFAULT_TOPIC = 'generalWellbeing'

const itemId = (item) => item?.id ?? item?._id

// Query patch that drops back from a detail page to its list.
const LEAVE_DETAIL = { kind: null, id: null, day: null }

/**
 * Everything needed to come back to a screen lives in the query string, so
 * detail pages can be linked, survive a refresh, and browser Back works:
 *   ?mode=Programs                         list
 *   ?mode=Programs&kind=Programs&id=…&day=7  detail (day is 1-based)
 *   ?mode=Programs&view=manage             running programs
 */
const readQuery = (search) => {
    const params = new URLSearchParams(search)
    const requestedMode = params.get('mode')
    const kind = params.get('kind')
    const id = params.get('id')
    const day = parseInt(params.get('day'), 10)
    const selected = MODES.includes(kind) && id ? { kind, id } : null
    return {
        mode: MODES.includes(requestedMode) ? requestedMode : DEFAULT_MODE,
        hasMode: MODES.includes(requestedMode),
        selected,
        day: Number.isFinite(day) && day > 0 ? day - 1 : 0,
        screen: selected ? 'single' : params.get('view') === 'manage' ? 'manage' : 'home',
    }
}

function ReadyToGo() {
    usePageTitle('Upstrive Education - Ready-to-go content')
    const history = useHistory()
    const location = useLocation()
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const { mode, hasMode, selected, day, screen } = useMemo(() => readQuery(location.search), [location.search])
    const [contentGroup, setContentGroup] = useState('type')
    const [selectedTopic, setSelectedTopic] = useState(DEFAULT_TOPIC)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedRunningProgram, setSelectedRunningProgram] = useState([])
    const [runningCount, setRunningCount] = useState(null)
    const { user } = useUser()
    // The item that was clicked, so the detail renders without a lookup. After
    // a refresh it is gone and the item is found by id instead.
    const openedItemRef = useRef(null)
    // True when this page pushed the detail entry, so "back" can pop it
    // instead of stacking another list entry on top.
    const pushedDetailRef = useRef(false)

    useTrackedSurface(contentGroup === 'topic' ? ANALYTICS_EVENTS.CONTENT_LIBRARY_TOPIC_VIEWED : ANALYTICS_EVENTS.CONTENT_LIBRARY_TYPE_VIEWED)
    useTrackedSurface(LIBRARY_TYPE_EVENTS[mode], {
        active: screen === 'home' && contentGroup === 'type',
    })
    useTrackedSurface(ANALYTICS_EVENTS.CONTENT_LIBRARY_CATEGORY_VIEWED, {
        active: screen === 'home' && contentGroup === 'topic',
        contentId: selectedTopic,
        content: t(selectedTopic),
    })

    /** Patch the query string; null/'' removes a key. Replaces unless `push`. */
    const updateQuery = useCallback(
        (patch, { push = false } = {}) => {
            const params = new URLSearchParams(history.location.search)
            Object.entries(patch).forEach(([key, value]) => (value == null || value === '' ? params.delete(key) : params.set(key, String(value))))
            const to = { pathname: history.location.pathname, search: `?${params.toString()}` }
            if (push) history.push(to)
            else history.replace(to)
        },
        [history]
    )

    // Count for the "Manage running programs" button; refreshed when a program
    // is stopped from the manage panel. Failures just hide the badge.
    const runningRequestRef = useRef(0)
    const refreshRunningCount = useCallback(async () => {
        if (!user) return
        const requestId = ++runningRequestRef.current
        try {
            const response = await getRunningPrograms({ user })
            if (requestId !== runningRequestRef.current) return
            setRunningCount(response.status === 200 && Array.isArray(response.data) ? response.data.length : null)
        } catch (err) {
            if (requestId === runningRequestRef.current) setRunningCount(null)
        }
    }, [user])
    useEffect(() => {
        refreshRunningCount()
    }, [refreshRunningCount])

    // Always show the active tab in the URL so a copied link reopens it.
    useEffect(() => {
        if (!hasMode) updateQuery({ mode })
    }, [hasMode, mode, updateQuery])

    const typeSource = useReadyToGoContent({ kind: mode, locale, enabled: contentGroup === 'type' })
    const topicSource = useReadyToGoContent({ kind: 'topic', param: selectedTopic, locale, enabled: contentGroup === 'topic' })

    const normalizedTerm = searchTerm.trim().toLowerCase()
    const typeItems = useMemo(() => filterList(typeSource.data, normalizedTerm), [typeSource.data, normalizedTerm])
    const topicItems = useMemo(() => filterTopicContent(topicSource.data, normalizedTerm), [topicSource.data, normalizedTerm])

    // Journals render from the list item itself, so after a refresh the list
    // is loaded (from the shared cache when possible) to find it by id.
    const journalSource = useReadyToGoContent({ kind: 'Journals', locale, enabled: selected?.kind === 'Journals' })

    const openCurrentlyRunningPrograms = useCallback(
        (runningPrograms) => {
            setSelectedRunningProgram((runningPrograms || []).map((item) => item.id))
            updateQuery({ ...LEAVE_DETAIL, view: 'manage' }, { push: true })
        },
        [updateQuery]
    )

    const handleManageRunningPrograms = () => {
        setSelectedRunningProgram([])
        updateQuery({ ...LEAVE_DETAIL, view: screen === 'manage' ? null : 'manage' }, { push: true })
    }

    const selectType = () => {
        setContentGroup('type')
        updateQuery({ ...LEAVE_DETAIL, view: null })
    }
    const selectTopic = () => {
        setContentGroup('topic')
        setSelectedTopic(DEFAULT_TOPIC)
        setSearchTerm('')
        updateQuery({ ...LEAVE_DETAIL, view: null })
    }
    const selectMode = (nextMode) => {
        setSearchTerm('')
        updateQuery({ ...LEAVE_DETAIL, view: null, mode: nextMode })
    }

    const openItem = useCallback(
        (item, kind) => {
            const id = itemId(item)
            if (id == null) {
                console.error('ReadyToGo: cannot open an item without an id', item)
                return
            }
            openedItemRef.current = { kind, id: String(id), item }
            pushedDetailRef.current = true
            setSearchTerm('')
            updateQuery({ kind, id, day: null, view: null }, { push: true })
        },
        [updateQuery]
    )

    const goHome = useCallback(() => {
        if (pushedDetailRef.current) {
            pushedDetailRef.current = false
            history.goBack()
        } else {
            updateQuery(LEAVE_DETAIL)
        }
    }, [history, updateQuery])

    const handleDayChange = useCallback((index) => updateQuery({ day: index > 0 ? index + 1 : null }), [updateQuery])

    const findSelectedItem = () => {
        const opened = openedItemRef.current
        if (opened && opened.kind === selected.kind && opened.id === selected.id) return opened.item
        if (selected.kind !== 'Journals') return null
        return (journalSource.data || []).find((item) => String(itemId(item)) === selected.id) ?? null
    }

    const renderSingle = () => {
        if (!selected) return null
        const { kind, id } = selected
        const item = findSelectedItem()
        if (kind === 'Journals' || item?.type === 'journal') {
            if (item) return <SingleJournal journal={item} setScreen={goHome} />
            if (journalSource.loading)
                return (
                    <div className="px-14 p-8">
                        <ContentSkeleton sections={1} />
                    </div>
                )
            return (
                <div className="rtg-empty flex flex-col items-center justify-center gap-4 py-[60px]">
                    <p className="text-[16px] text-darkenPurple font-semibold">{t('No data found')}</p>
                    <button type="button" className="showMoreLessButton" onClick={goHome}>
                        {t('back')}
                    </button>
                </div>
            )
        }
        if (kind === 'Surveys') return <SingleSurvey surveyID={id} setScreen={goHome} />
        return <SingleProgram programID={id} setScreen={goHome} initialDay={day} onDayChange={handleDayChange} />
    }

    const showManageButton = contentGroup === 'topic' || mode === 'Programs'

    return (
        <div className="page-shell bg-lightBG h-full min-w-0 flex flex-col overflow-y-scroll">
            <div className="relative h-max flex justify-center items-center">
                <div className="rtg-header bg-white w-full max-w-[1200px] rounded-xl flex flex-col">
                    <div className="flex flex-row flex-wrap items-end gap-4">
                        <div className="flex-1">
                            <h1 className="page-title text-darkenPurple font-bold">{t('readyToGoContent')} </h1>
                        </div>

                        <div className="flex flex-row items-center">
                            <span className="text-[#9392AD] text-[18px] font-semibold mr-[14px]">{t('sortBy')} :</span>
                            <div className="rtg-toggle bg-[#F8F8FB] h-[50px] items-center justify-center w-[220px] rounded-[100px] flex flex-row" data-active={contentGroup}>
                                <div className="rtg-toggle-pill" aria-hidden="true" />
                                <button
                                    type="button"
                                    aria-pressed={contentGroup === 'type'}
                                    className={`rtg-toggle-button flex-1 font-bold text-[18px] rounded-[100px] h-[50px] ${contentGroup === 'type' ? 'text-[#FDDF6D]' : 'text-[#9392AD]'}`}
                                    onClick={selectType}
                                >
                                    {t('type')}
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={contentGroup === 'topic'}
                                    className={`rtg-toggle-button flex-1 font-bold text-[18px] rounded-[100px] h-[50px] ${contentGroup === 'topic' ? 'text-[#FDDF6D]' : 'text-[#9392AD]'}`}
                                    onClick={selectTopic}
                                >
                                    {t('topic')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <p className="text-base text-mudPurple">{t('evidenceBasedContentPreparedForYouByOurTeamOfExperts')} </p>
                    {contentGroup === 'type' && (
                        <div className="grid grid-cols-3 gap-6 mt-8">
                            {CONTENT_LIST.map((content) => {
                                const isSelected = mode === content.name
                                return (
                                    <button
                                        type="button"
                                        id={`ready-to-go-content-button-${content.name}`}
                                        className="rtg-mode-button relative flex justify-center"
                                        aria-pressed={isSelected}
                                        onClick={() => selectMode(content.name)}
                                        key={content.name}
                                    >
                                        <div
                                            className={`rtg-mode-card p-7 pointer rounded-xl w-full flex flex-col border-2 border-[#5E639B] h-full ${
                                                isSelected ? 'bg-[#5E639B]' : 'bg-white hover:bg-lightBG'
                                            }`}
                                        >
                                            <div className="flex flex-row justify-start items-center">
                                                {React.cloneElement(content.icon, { fill: isSelected ? '#FDDF6D' : '#5E639B' })}
                                                <span className={`ml-3 font-bold text-xl ${isSelected ? 'text-[#FDDF6D]' : 'text-deepPurple'}`}>{t(content.name)}</span>
                                            </div>
                                            <p className={`rtg-mode-desc text-base text-left mt-4 ${isSelected ? 'text-white' : 'text-darkenPurple'}`}>{t(content.description)}</p>
                                        </div>
                                        {isSelected && (
                                            <div
                                                className="rtg-mode-arrow w-0 h-0 absolute"
                                                style={{
                                                    borderRight: '20px solid transparent',
                                                    borderLeft: '20px solid transparent',
                                                    borderTop: '20px solid #5E639B',
                                                    bottom: -20,
                                                }}
                                            ></div>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                    {contentGroup === 'topic' && <ContentTopicButtons selectTopic={setSelectedTopic} selectedTopic={selectedTopic} />}
                    <div className="w-full flex flex-row flex-wrap gap-4 items-center justify-center mt-[40px] pt-[25px]" style={{ borderTop: '1px dashed #BEBECE' }}>
                        <div className="rtg-search w-full max-w-[341px] h-[50px] flex flex-row border border-deselectPurple rounded-full items-center pr-[17px]">
                            <input
                                id="ready-to-go-search-input"
                                type="text"
                                placeholder={t('search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-full p-4 flex-1 rounded-full"
                                autoComplete="off"
                            />
                            <button
                                type="button"
                                className="rtg-search-clear"
                                data-visible={searchTerm.length > 0}
                                aria-label={t('Clear')}
                                tabIndex={searchTerm.length > 0 ? 0 : -1}
                                onClick={() => setSearchTerm('')}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                                    <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                            <SearchMagnifyIcon />
                        </div>
                        <div className="rtg-search-spacer flex-1"></div>
                        {showManageButton && (
                            <button
                                type="button"
                                id="manage-running-programs-button"
                                aria-pressed={screen === 'manage'}
                                className={`rtg-manage-button pointer flex items-center justify-center font-semibold text-lg rounded-full h-[50px] w-full max-w-[334px] ${
                                    screen === 'manage' ? 'bg-deepPurple text-white hover:text-highlightColor' : 'bg-white border border-deepPurple text-deepPurple hover:bg-lightBG'
                                }`}
                                onClick={handleManageRunningPrograms}
                            >
                                {t('manageRunningPrograms')}
                                {runningCount > 0 && <span className="rtg-count">{runningCount}</span>}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Keyed on `screen` so each panel swap fades in; mode/topic changes
                inside the home screen animate at the list level instead. */}
            <div className="w-full flex justify-center mt-[30px] rtg-panel-enter" key={screen}>
                {screen === 'single' && <div className="bg-white w-full max-w-[1200px] rounded-xl flex flex-col h-max">{renderSingle()}</div>}
                {screen === 'home' && contentGroup === 'type' && normalizedTerm && (
                    <SearchResults
                        items={typeItems}
                        type={mode}
                        term={searchTerm.trim()}
                        onClick={openItem}
                        onClear={() => setSearchTerm('')}
                        openCurrentlyRunningPrograms={openCurrentlyRunningPrograms}
                        loading={typeSource.loading}
                    />
                )}
                {screen === 'home' && contentGroup === 'type' && !normalizedTerm && (
                    <TypeHomeList
                        content={typeItems}
                        type={mode}
                        onClick={openItem}
                        openCurrentlyRunningPrograms={openCurrentlyRunningPrograms}
                        loading={typeSource.loading}
                        version={typeSource.version}
                    />
                )}
                {screen === 'home' && contentGroup === 'topic' && (
                    <TopicHomeList
                        content={topicItems}
                        term={searchTerm.trim()}
                        onClear={() => setSearchTerm('')}
                        onClick={openItem}
                        openCurrentlyRunningPrograms={openCurrentlyRunningPrograms}
                        loading={topicSource.loading}
                        version={topicSource.version}
                    />
                )}
                {screen === 'manage' && <RunningProgram selectedRunningProgram={selectedRunningProgram} onProgramsChanged={refreshRunningCount} />}
            </div>
        </div>
    )
}

export default ReadyToGo
